import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import { Link as RouterLink } from 'react-router-dom';
import { Mail, Briefcase, Star, Cog, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import UserMenu from './auth/UserMenu.tsx';
import AuthModal from './auth/AuthModal';

const pageLinks = [
  { name: 'Pricing', path: '/pricing' },
  { name: 'Blog', path: '/blog' },
];

const productInfoDropdownItems = [
  { name: 'Features', path: '/features', icon: Star },
  { name: 'How It Works', path: '/how-it-works', icon: Cog },
  { name: 'Testimonials', path: '/testimonials', icon: Users },
];

const contactDropdownItems = [
  { name: 'Contact Us', path: '/contact', icon: Mail },
  { name: 'Jobs', path: '/careers', icon: Briefcase },
];

function Header() {
  const { isAuthenticated } = useAuth();
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [showProductInfoDropdown, setShowProductInfoDropdown] = useState(false);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleProductInfoMouseEnter = () => {
    setShowProductInfoDropdown(true);
  };

  const handleProductInfoMouseLeave = () => {
    setTimeout(() => {
      setShowProductInfoDropdown(false);
    }, 200);
  };

  const handleProductInfoDropdownMouseEnter = () => {
    setShowProductInfoDropdown(true);
  };

  const handleProductInfoDropdownMouseLeave = () => {
    setShowProductInfoDropdown(false);
  };

  const handleContactMouseEnter = () => {
    setShowContactDropdown(true);
  };

  const handleContactMouseLeave = () => {
    setTimeout(() => {
      setShowContactDropdown(false);
    }, 200);
  };

  const handleContactDropdownMouseEnter = () => {
    setShowContactDropdown(true);
  };

  const handleContactDropdownMouseLeave = () => {
    setShowContactDropdown(false);
  };

  const handleLoginClick = () => {
    setAuthModalOpen(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); 
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={scrolled ? 4 : 0}
        sx={{ 
          backgroundColor: scrolled ? 'rgb(21,35,126)' : 'rgb(21,35,126)',
          transition: 'all 0.3s ease-in-out',
          zIndex: (theme) => theme.zIndex.drawer + 1, 
        }}
      >
        <Container maxWidth="lg">
<Toolbar 
  disableGutters
  sx={{
    minHeight: { xs: 48, sm: 56 }, // Even more compact
    height: { xs: 48, sm: 56 },
    py: 0,
  }}
>
  <Box 
    component={RouterLink}
    to="/"
    sx={{ 
      display: { xs: 'none', md: 'flex' },
      alignItems: 'center',
      textDecoration: 'none',
      color: scrolled ? 'white' : 'white',
      '&:hover': {
        backgroundColor: scrolled ? 'rgba(63, 81, 181, 0.08)' : 'rgba(255, 255, 255, 0.1)',
      },
      borderRadius: 1,
      px: 1,
      py: 0.5,
      transition: 'background-color 0.2s ease-in-out',
    }}
  >
    <Box 
      component="img" 
      src="/assets/RIC_NoText.png" 
      alt="RecruitAI Logo" 
      sx={{ 
        width: 28, // Slightly smaller
        height: 28, // Slightly smaller
        mr: 2,
        display: { xs: 'none', md: 'flex' }
      }} 
    />
    <Typography
      variant="h6"
      noWrap
      sx={{
        mr: 2,
        display: { xs: 'none', md: 'flex' },
        fontWeight: 700,
        color: scrolled ? 'white' : 'white',
        textDecoration: 'none',
        fontSize: '1.1rem', // Slightly smaller font
      }}
    >
      RECRUITMENT INTELLIGENCE<span style={{ fontSize: '1.0em', verticalAlign: 'super' }}>®</span>
    </Typography>
  </Box>

            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                disableRipple
                disableFocusRipple
                sx={{
                  color: 'white',
                  WebkitTapHighlightColor: 'transparent',
                  '&:focus,&:focus-visible': { outline: 'none', backgroundColor: 'transparent' },
                  '& .MuiTouchRipple-root': { display: 'none' },
                }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' },
                }}
              >
                {productInfoDropdownItems.map((item) => (
                  <MenuItem 
                    key={item.name} 
                    component={RouterLink}
                    to={item.path}
                    onClick={handleCloseNavMenu}
                  >
                    <Typography textAlign="center">{item.name}</Typography>
                  </MenuItem>
                ))}
                {pageLinks.map((page) => (
                  <MenuItem 
                    key={page.name} 
                    component={RouterLink}
                    to={page.path}
                    onClick={handleCloseNavMenu}
                  >
                    <Typography textAlign="center">{page.name}</Typography>
                  </MenuItem>
                ))}
                {contactDropdownItems.map((item) => (
                  <MenuItem 
                    key={item.name} 
                    component={RouterLink}
                    to={item.path}
                    onClick={handleCloseNavMenu}
                  >
                    <Typography textAlign="center">{item.name}</Typography>
                  </MenuItem>
                ))}
               {!isAuthenticated ? (
            // if not logged in → show Login
            <MenuItem onClick={() => { handleCloseNavMenu(); handleLoginClick(); }}>
              <Typography textAlign="center">Login</Typography>
            </MenuItem>
              ) : (
                // else (if logged in) → show user menu items
                <>
                  <MenuItem component={RouterLink} to="/dashboard" onClick={handleCloseNavMenu}>
                    <Typography textAlign="center">Dashboard</Typography>
                  </MenuItem>
                  <MenuItem component={RouterLink} to="/profile" onClick={handleCloseNavMenu}>
                    <Typography textAlign="center">Profile</Typography>
                  </MenuItem>
                  <MenuItem component={RouterLink} to="/subscription" onClick={handleCloseNavMenu}>
                    <Typography textAlign="center">Subscription</Typography>
                  </MenuItem>
                  <MenuItem component={RouterLink} to="/settings" onClick={handleCloseNavMenu}>
                    <Typography textAlign="center">Settings</Typography>
                  </MenuItem>
                  <MenuItem component={RouterLink} to="/security" onClick={handleCloseNavMenu}>
                    <Typography textAlign="center">Security</Typography>
                  </MenuItem>                  
                  <MenuItem onClick={() => { handleCloseNavMenu(); /* call signOut() here */ }}>
                    <Typography textAlign="center">Sign out</Typography>
                  </MenuItem>
                </>
              )}
              </Menu>
            </Box>
            
            <Box 
              component={RouterLink}
              to="/"
              sx={{ 
                display: { xs: 'flex', md: 'none' },
                flexGrow: 1,
                alignItems: 'center',
                textDecoration: 'none',
                color: scrolled ? 'white' : 'white',
                '&:hover': {
                  backgroundColor: scrolled ? 'rgba(63, 81, 181, 0.08)' : 'rgba(255, 255, 255, 0.1)',
                },
                borderRadius: 1,
                px: 1,
                py: 0.5,
                transition: 'background-color 0.2s ease-in-out',
              }}
            >
              <Box 
                component="img" 
                src="/assets/RIC_NoText.png" 
                alt="RecruitAI Logo" 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  mr: 2,
                  display: { xs: 'none', md: 'flex' }
                }} 
              />
              <Typography
                variant="h5"
                noWrap
                sx={{
                  fontWeight: 700,
                }}
              >
                RECRUITMENT INTELLIGENCE<span style={{ fontSize: '1.0em', verticalAlign: 'super' }}>®</span>
              </Typography>
            </Box>
            
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center' }}>
              {/* Product Info Dropdown */}
              <Box
                onMouseEnter={handleProductInfoMouseEnter}
                onMouseLeave={handleProductInfoMouseLeave}
                sx={{ position: 'relative' }}
              >
                <Button
                  sx={{ 
                    my: 2, 
                    mx: 1,
                    color: scrolled ? 'white' : 'white',
                    display: 'block',
                    '&:hover': {
                      backgroundColor: scrolled ? 'rgba(63, 81, 181, 0.08)' : 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  Product Info
                </Button>
                
                {showProductInfoDropdown && (
                  <Paper
                    onMouseEnter={handleProductInfoDropdownMouseEnter}
                    onMouseLeave={handleProductInfoDropdownMouseLeave}
                    elevation={8}
                    sx={{
                      position: 'absolute',
                      top: 'calc(100% - 4px)',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      minWidth: '200px',
                      zIndex: (theme) => theme.zIndex.modal,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '-4px',
                        left: 0,
                        right: 0,
                        height: '4px',
                        backgroundColor: 'transparent',
                      }
                    }}
                  >
                    {productInfoDropdownItems.map((item, index) => {
                      const IconComponent = item.icon;
                      return (
                        <Box
                          key={item.name}
                          component={RouterLink}
                          to={item.path}
                          onClick={() => setShowProductInfoDropdown(false)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 3,
                            py: 2,
                            textDecoration: 'none',
                            color: 'text.primary',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(21, 35, 126, 0.08)',
                              transform: 'translateX(2px)',
                            },
                            borderBottom: index !== productInfoDropdownItems.length - 1 ? '1px solid' : 'none',
                            borderColor: 'rgba(0,0,0,0.06)',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 20,
                              height: 20,
                              mr: 2,
                              color: 'rgb(21, 35, 126)',
                            }}
                          >
                            <IconComponent size={16} />
                          </Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              fontSize: '0.875rem',
                            }}
                          >
                            {item.name}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Paper>
                )}
              </Box>

              {/* Regular menu items */}
              {pageLinks.map((page) => (
                <Button
                  key={page.name}
                  component={RouterLink}
                  to={page.path}
                  onClick={handleCloseNavMenu}
                  sx={{ 
                    my: 2, 
                    mx: 1,
                    color: scrolled ? 'white' : 'white',
                    display: 'block',
                    '&:hover': {
                      backgroundColor: scrolled ? 'rgba(63, 81, 181, 0.08)' : 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  {page.name}
                </Button>
              ))}
              
              {/* Contact Dropdown */}
              <Box
                onMouseEnter={handleContactMouseEnter}
                onMouseLeave={handleContactMouseLeave}
                sx={{ position: 'relative' }}
              >
                <Button
                  sx={{ 
                    my: 2, 
                    mx: 1,
                    color: scrolled ? 'white' : 'white',
                    display: 'block',
                    '&:hover': {
                      backgroundColor: scrolled ? 'rgba(63, 81, 181, 0.08)' : 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  Contact
                </Button>
                
                {showContactDropdown && (
                  <Paper
                    onMouseEnter={handleContactDropdownMouseEnter}
                    onMouseLeave={handleContactDropdownMouseLeave}
                    elevation={8}
                    sx={{
                      position: 'absolute',
                      top: 'calc(100% - 4px)',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      minWidth: '180px',
                      zIndex: (theme) => theme.zIndex.modal,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '-4px',
                        left: 0,
                        right: 0,
                        height: '4px',
                        backgroundColor: 'transparent',
                      }
                    }}
                  >
                    {contactDropdownItems.map((item, index) => {
                      const IconComponent = item.icon;
                      return (
                        <Box
                          key={item.name}
                          component={RouterLink}
                          to={item.path}
                          onClick={() => setShowContactDropdown(false)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 3,
                            py: 2,
                            textDecoration: 'none',
                            color: 'text.primary',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(21, 35, 126, 0.08)',
                              transform: 'translateX(2px)',
                            },
                            borderBottom: index !== contactDropdownItems.length - 1 ? '1px solid' : 'none',
                            borderColor: 'rgba(0,0,0,0.06)',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 20,
                              height: 20,
                              mr: 2,
                              color: 'rgb(21, 35, 126)',
                            }}
                          >
                            <IconComponent size={16} />
                          </Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              fontSize: '0.875rem',
                            }}
                          >
                            {item.name}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Paper>
                )}
              </Box>
            </Box>

            {/* Auth Button or User Menu */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              {isAuthenticated ? (
                <UserMenu />
              ) : (
                <Button
                  onClick={handleLoginClick}
                  sx={{
                    my: 2,
                    mx: 1,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  Login
                </Button>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialView="signin"
      />
    </>
  );
}

export default Header;